import { useRef } from 'react';
import { Typography } from '@equinor/eds-core-react';
import styled from 'styled-components';
import { CenteredLoader } from '../../../../components/CenteredLoader/CenteredLoader';
import { analogueModelDefault } from '../../../../constants/analogueModelDefault';
import { useFetchModel } from '../../../../hooks/useFetchModel';
import { theme } from '../../../../tokens/theme';
import { ArchelCanvas } from '../ArchelCanvas/ArchelCanvas';
import { useArchelGrid } from '../hooks/useArchelGrid';
import {
  createModelAreasStore,
  ModelAreasStoreContext,
} from '../hooks/useModelAreasStore';
import { buildArchelMeta } from '../modelAreaCoordinates.utils';
import { useFetchGrossDepData } from '../../../../hooks/useFetchGrossDepData';

const Wrapper = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
`;

const LegendColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const Swatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
  border: 1px solid ${theme.light.ui.background.medium};
`;

/** Read-only model image viewer: z-layer slider + archel color legend. */
export const ModelImageDisplay = () => {
  const { data: analogueModel = analogueModelDefault } = useFetchModel();
  const { data: geologyStandards } = useFetchGrossDepData();
  const storeRef = useRef(createModelAreasStore());

  const modelId = analogueModel.analogueModelId || undefined;
  const archels = buildArchelMeta(
    analogueModel.iniParameters?.archels ?? [],
    geologyStandards,
  );
  const gridQuery = useArchelGrid(modelId);

  const visibleArchels = gridQuery.data
    ? archels.filter((a) => gridQuery.data.presentArchelIds.has(a.id))
    : archels;

  if (gridQuery.isLoading) {
    return <CenteredLoader size={48} />;
  }

  if (gridQuery.error) {
    return (
      <Typography variant="body_short">
        Error: {String(gridQuery.error)}
      </Typography>
    );
  }

  return (
    <ModelAreasStoreContext.Provider value={storeRef.current}>
      <Wrapper>
        <ArchelCanvas
          gridData={gridQuery.data}
          loading={false}
          error={null}
          archels={archels}
          showAxis={true}
          maxWidth={650}
          readOnly
        />
        {visibleArchels.length > 0 && (
          <LegendColumn>
            {visibleArchels.map((a) => (
              <LegendItem key={a.id}>
                <Swatch $color={a.color} />
                <Typography variant="caption">{a.name}</Typography>
              </LegendItem>
            ))}
          </LegendColumn>
        )}
      </Wrapper>
    </ModelAreasStoreContext.Provider>
  );
};
